// src/components/JarvislyModule/PageHeader/CardHeaderAvatar.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ColorPicker, Dropdown, Modal, Tooltip, Upload } from 'antd';
import {
  CameraOutlined,
  DeleteOutlined,
  PictureOutlined,
  SmileOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import utils from 'utils/utils';
import useFormElement from 'hooks/useFormElement';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { generateHexColor } from 'utils/colors';
import {
  blobToFile,
  makeBase64Thumb,
  readFileAsDataURL,
  resizeImage,
  uniqueName,
} from 'utils/imageUtils';
import { useModuleStore } from 'stores/module';
import {
  DEFAULT_BACK_COLOR,
  DEFAULT_BACK_COLOR_ACCENT,
  DEFAULT_FORE_COLOR,
  DEFAULT_BACK_COLOR_ACCENT_DARK,
  DEFAULT_FORE_COLOR_DARK,
} from 'constants/Constants';
import useZustand from 'hooks/useZustand';
import AvatarDicebearModal from 'layouts/SharedComponents/AvatarDicebearModal';
import AvatarCropModal from 'layouts/SharedComponents/AvatarCropModal';
import WebcamCaptureModal from 'layouts/SharedComponents/WebcamCaptureModal';
import appService from 'services/appService';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import { useModuleContext } from 'components/core/ModuleProvider';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// Detect if device is mobile/tablet (touch device)
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CardHeaderAvatar = ({
  subdocumentField,
  record,
  lockAvatar,
  avatarName,
  avatarNamePrecedence,
  restProps,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1) PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Get setAdditionalFields and additionalFields from SimpleFormContext (may be undefined if not inside SimpleFormProvider)
  const formContext = useSimpleFormContext();
  const setAdditionalFields = formContext?.setAdditionalFields;
  const additionalFields = formContext?.additionalFields;

  // Get avatarInitial from ModuleContext (set by SimpleInput with useInitialInAvatar)
  const { avatarInitial } = useModuleContext();

  const { data } = useDocumentContext();
  const documentId = data?._id;
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDarkMode = currentTheme === 'dark';

  // Get avatar config from module
  const storedCurrentPage = useZustand('module', 'currentPage');
  const avatarConfig = storedCurrentPage?.header?.avatar || {};
  const noShowInitials = avatarConfig.noShowInitials === true;
  const noShowColor = avatarConfig.noShowColor === true;
  const hideToolbar = avatarConfig.hideToolbar === true;

  // Get default colors based on theme
  const defaultBackColor = isDarkMode ? DEFAULT_BACK_COLOR_ACCENT_DARK : DEFAULT_BACK_COLOR_ACCENT;
  const defaultForeColor = isDarkMode ? DEFAULT_FORE_COLOR_DARK : DEFAULT_FORE_COLOR;

  const { flattenFieldName: backColorFlattenFieldName } = useFormElement(
    'backColor',
    subdocumentField
  );
  const { flattenFieldName: foreColorFlattenFieldName } = useFormElement(
    'foreColor',
    subdocumentField
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) DERIVED STATE & MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const title = useMemo(() => (avatarName ? avatarName : record?.name), [avatarName, record?.name]);
  const initials = useMemo(
    () =>
      utils.getNameInitial(avatarNamePrecedence || record?.name || avatarInitial || title) || '',
    [avatarInitial, title, avatarNamePrecedence, record?.name]
  );

  const hasInitials = initials.trim().length > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3) REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const uploadRef = useRef(null);
  const cameraRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [backColor, setBackColor] = useState(defaultBackColor);
  const [foreColor, setForeColor] = useState(defaultForeColor);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [isLoadingHQ, setIsLoadingHQ] = useState(false); // Loading high-quality image
  const [dicebearModalOpen, setDicebearModalOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [avatarRemoved, setAvatarRemoved] = useState(false);
  const [webcamModalOpen, setWebcamModalOpen] = useState(false);
  const [imageFromWebcam, setImageFromWebcam] = useState(false); // Track if image came from webcam

  // ─────────────────────────────────────────────────────────────────────────────
  // 5) EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // ─────────────────────────────────────────────────────────────────────────────
    // Determine colors based on record presence and current theme
    // ─────────────────────────────────────────────────────────────────────────────
    const resolvedBackColor = record
      ? record?.backColor || getValueByPath(record, backColorFlattenFieldName) || defaultBackColor
      : defaultBackColor;

    const resolvedForeColor = record
      ? record?.foreColor || getValueByPath(record, foreColorFlattenFieldName) || defaultForeColor
      : defaultForeColor;

    setBackColor(resolvedBackColor);
    setForeColor(resolvedForeColor);

    if (setAdditionalFields) {
      setAdditionalFields(prev => ({
        ...prev,
        [backColorFlattenFieldName]: resolvedBackColor,
        [foreColorFlattenFieldName]: resolvedForeColor,
      }));
    }
  }, [
    record,
    backColorFlattenFieldName,
    foreColorFlattenFieldName,
    setAdditionalFields,
    defaultBackColor,
    defaultForeColor,
  ]);

  // CRITICAL: Reset preview when NAVIGATING to a different document
  // Skip reset when documentId becomes undefined (duplication) - keep the avatar
  const prevDocumentIdRef = useRef(documentId);
  useEffect(() => {
    const prevId = prevDocumentIdRef.current;
    prevDocumentIdRef.current = documentId;

    // Only reset when navigating to a DIFFERENT document (both IDs are defined and different)
    // Don't reset when:
    // - documentId becomes undefined (duplication/new)
    // - first render (prevId is undefined)
    if (prevId && documentId && prevId !== documentId) {
      setPreviewSrc('');
      setAvatarRemoved(false);
      setIsLoadingHQ(false);
    }
  }, [documentId]);

  // CRITICAL FIX: Recover avatar from additionalFields after component remount
  // This handles the case where the component is remounted during upload
  // and loses its local state (previewSrc), but additionalFields still has the data
  useEffect(() => {
    // Skip if we already have a preview
    if (previewSrc) return;

    // Check if additionalFields has avatar data (set during upload)
    // Priority: final URLs > preview URL (DiceBear URL during upload)
    const thumbUrl = additionalFields?.['avatar.thumbUrl'];
    const largeUrl = additionalFields?.['avatar.largeUrl'];
    const base64Thumb = additionalFields?.['avatar.base64Thumb'];
    const previewUrl = additionalFields?.['avatar.previewUrl']; // DiceBear URL during upload

    const recoveredUrl = thumbUrl || largeUrl || base64Thumb || previewUrl;
    if (recoveredUrl) {
      setPreviewSrc(recoveredUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalFields]);

  // Load avatar with progressive loading: show base64Thumb immediately, then load HQ image
  // Priority: entity avatar → server-side linked account avatar
  // Only show loading on initial load, not on refresh (when previewSrc already exists)
  useEffect(() => {
    const avatar = data?.avatar || data?.__accountLinked?.avatar;
    if (!avatar) {
      // Clear preview when no avatar source exists — skip if uploading or pending link has avatar
      const pendingAvatarSrc = additionalFields?.['__accountLink.avatarSrc'];
      if (!uploading && !pendingAvatarSrc) {
        setPreviewSrc('');
      }
      return;
    }

    const { base64Thumb, thumbUrl, storageUrl } = avatar;
    const hqUrl = thumbUrl || storageUrl;

    // Check if this is an initial load (no previewSrc yet) or a refresh
    const isInitialLoad = !previewSrc;

    // If we have base64Thumb and a HQ URL, do progressive loading
    if (base64Thumb && hqUrl) {
      // Show low-quality immediately
      setPreviewSrc(base64Thumb);

      // Only show loading indicator on initial load, not on refresh
      if (isInitialLoad) {
        setIsLoadingHQ(true);
      }

      // Preload HQ image in background
      const img = new Image();
      img.onload = () => {
        setPreviewSrc(hqUrl);
        setIsLoadingHQ(false);
      };
      img.onerror = () => {
        // Keep base64Thumb on error
        setIsLoadingHQ(false);
      };
      img.src = hqUrl;
    } else if (hqUrl) {
      // No base64Thumb, just use HQ directly
      setPreviewSrc(hqUrl);
    } else if (base64Thumb) {
      // Only base64Thumb available
      setPreviewSrc(base64Thumb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.avatar, data?.__accountLinked?.avatar]);

  // React to pending account link/unlink from SystemUserLinkCard
  // Shows/hides linked account avatar immediately when user clicks Link/Unlink (before save)
  useEffect(() => {
    // Skip if entity has its own avatar (always takes priority)
    if (data?.avatar) return;

    const pendingAction = additionalFields?.['__accountLink.action'];
    const pendingAvatarSrc = additionalFields?.['__accountLink.avatarSrc'];

    if (pendingAction === 'link' && pendingAvatarSrc) {
      // Linking: show linked account avatar immediately
      setPreviewSrc(pendingAvatarSrc);
    } else if (pendingAction === 'unlink') {
      // Unlinking: clear avatar immediately
      setPreviewSrc('');
    } else if (!pendingAction) {
      // No pending action (cancelled or initial state): revert to server data
      const serverAvatar = data?.__accountLinked?.avatar;
      if (serverAvatar) {
        setPreviewSrc(serverAvatar.base64Thumb || serverAvatar.thumbUrl || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    additionalFields?.['__accountLink.action'],
    additionalFields?.['__accountLink.avatarSrc'],
    data?.avatar,
    data?.__accountLinked?.avatar,
  ]);
  // ─────────────────────────────────────────────────────────────────────────────
  // 6) HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const onChangeColor = color => {
    // keep colors in sync with readable contrast
    const hexBackColor = color.toHexString();
    const hexForeColor = generateHexColor(hexBackColor, true);
    setBackColor(hexBackColor);
    setForeColor(hexForeColor);
    setAdditionalFields?.(prev => ({
      ...prev,
      [backColorFlattenFieldName]: hexBackColor,
      [foreColorFlattenFieldName]: hexForeColor,
    }));
  };

  const validateFile = file => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      appService.message({ flag: 'e', text: translateX('invalid_image_type') });
      return false;
    }
    if (file.size > MAX_FILE_BYTES) {
      appService.message({ flag: 'e', text: translateX('file_too_large_max_2mb') });
      return false;
    }
    return true;
  };

  const handleProcessedImage = async croppedFile => {
    try {
      setUploading(true);
      const baseName = `avatar-${documentId || 'tmp'}`;
      const ext = (croppedFile.type.split('/')[1] || 'jpg').toLowerCase();

      const dataURL = await readFileAsDataURL(croppedFile);

      // large
      const largeBlob = await resizeImage(dataURL, {
        maxW: 1600,
        maxH: 1600,
        mime: 'image/jpeg',
        quality: 0.85,
      });
      const largeFile = blobToFile(largeBlob, uniqueName(`${baseName}-large`, ext));

      // thumb
      const thumbBlob = await resizeImage(dataURL, {
        maxW: 256,
        maxH: 256,
        mime: 'image/jpeg',
        quality: 0.75,
      });
      const thumbFile = blobToFile(thumbBlob, uniqueName(`${baseName}-thumb`, ext));

      // base64 list thumb
      const base64Thumb = await makeBase64Thumb(dataURL, {
        size: 128,
        mime: 'image/jpeg',
        quality: 0.7,
      });

      // paths
      const largePath = buildAvatarImagePath({
        documentId: documentId || 'temp',
        variant: 'large',
        filename: largeFile.name,
      });
      const thumbPath = buildAvatarImagePath({
        documentId: documentId || 'temp',
        variant: 'thumb',
        filename: thumbFile.name,
      });

      // upload in parallel
      const [{ url: largeUrl }, { url: thumbUrl }] = await Promise.all([
        uploadFile(largeFile, largePath),
        uploadFile(thumbFile, thumbPath),
      ]);

      // update extras
      setAdditionalFields?.(prev => ({
        ...prev,
        'avatar.largeUrl': largeUrl,
        'avatar.thumbUrl': thumbUrl,
        'avatar.base64Thumb': base64Thumb,
      }));

      setPreviewSrc(thumbUrl);
      appService.message({ flag: 's', text: translateX('image_uploaded_successfully') });
    } catch (err) {
      // log for debugging
      console.error(err);
      appService.message({ flag: 'e', text: translateX('image_upload_failed') });
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = file => {
    if (!validateFile(file)) return Upload.LIST_IGNORE;
    // Open crop modal instead of processing directly
    setSelectedImageFile(file);
    setImageFromWebcam(false); // Mark that this image came from file upload, not webcam
    setCropModalOpen(true);
    return false; // Prevent default upload
  };

  const handleCropConfirm = avatarData => {
    // Same pattern as handleDicebearConfirm
    // Error call: { isUploading: false } - upload failed
    if (!avatarData) return;

    const { previewUrl, isUploading, largeUrl, thumbUrl, base64Thumb, attachmentId } = avatarData;

    // Reset avatarRemoved since we're adding a new avatar
    setAvatarRemoved(false);

    if (isUploading) {
      setPreviewSrc(previewUrl);
      setCropModalOpen(false);
      setUploading(true);

      // CRITICAL FIX: Also save preview URL to additionalFields
      // This ensures the preview can be recovered if component remounts during upload
      setAdditionalFields?.(prev => ({
        ...prev,
        'avatar._deleted': undefined,
        'avatar.previewUrl': previewUrl, // Temporary preview URL (base64 or blob URL)
      }));
    } else {
      // Upload finished (success or failure)
      setUploading(false);
      setSelectedImageFile(null);

      // Only update data if we have actual results (not a failure)
      if (largeUrl || thumbUrl || base64Thumb) {
        // IMPORTANT: Prefer base64Thumb for immediate display (more reliable)
        setPreviewSrc(base64Thumb || thumbUrl || largeUrl);
        setAdditionalFields?.(prev => ({
          ...prev,
          'avatar._deleted': undefined, // Clear any previous deletion flag
          'avatar.largeUrl': largeUrl,
          'avatar.thumbUrl': thumbUrl,
          'avatar.base64Thumb': base64Thumb,
          'avatar.attachmentId': attachmentId,
          'avatar.previewUrl': undefined, // Clear temporary preview URL
        }));
      }
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setSelectedImageFile(null);
    setImageFromWebcam(false);
  };

  // Check if there's an existing avatar (from data or preview)
  // Use avatarRemoved state to track local removal before save
  const hasExistingAvatar = !avatarRemoved && !!(previewSrc || data?.avatar?.attachmentId || data?.__accountLinked?.avatar?.attachmentId);
  const isMobile = isMobileDevice();

  // Menu items - camera available on both desktop (webcam) and mobile (native camera)
  const items = [
    { key: 'upload', label: translateX('upload_image'), icon: <UploadOutlined /> },
    { key: 'camera', label: translateX('take_photo'), icon: <CameraOutlined /> },
    { key: 'avatar', label: translateX('choose_avatar'), icon: <SmileOutlined /> },
    // Only show remove option if there's an existing avatar
    ...(hasExistingAvatar
      ? [
          { type: 'divider' },
          {
            key: 'remove',
            label: translateX('remove_avatar'),
            icon: <DeleteOutlined />,
            danger: true,
          },
        ]
      : []),
  ];

  const onMenuClick = ({ key }) => {
    if (key === 'upload') {
      uploadRef.current?.querySelector('input[type="file"]')?.click();
    } else if (key === 'camera') {
      // On mobile: use native camera input
      // On desktop: open webcam modal
      if (isMobile) {
        cameraRef.current?.querySelector('input[type="file"]')?.click();
      } else {
        setWebcamModalOpen(true);
      }
    } else if (key === 'avatar') {
      setDicebearModalOpen(true);
    } else if (key === 'remove') {
      handleRemoveAvatar();
    }
  };

  const handleRemoveAvatar = () => {
    // Clear the preview and mark as removed locally
    setPreviewSrc('');
    setAvatarRemoved(true);

    // Get the existing attachment _id from data.attachments array
    const existingAvatarAttachment = data?.attachments?.find(
      att => att.attachmentType === 'avatar'
    );

    // Mark avatar as deleted for backend processing
    setAdditionalFields?.(prev => ({
      ...prev,
      'avatar._deleted': true,
      'avatar._id': existingAvatarAttachment?._id || null,
      'avatar.attachmentId':
        data?.avatar?.attachmentId || existingAvatarAttachment?.attachmentId || null,
      // Clear other avatar fields
      'avatar.largeUrl': null,
      'avatar.thumbUrl': null,
      'avatar.base64Thumb': null,
    }));
  };

  const handleWebcamCapture = file => {
    // Close webcam modal and open crop modal with captured image
    setWebcamModalOpen(false);
    if (file && validateFile(file)) {
      setSelectedImageFile(file);
      setImageFromWebcam(true); // Mark that this image came from webcam
      setCropModalOpen(true);
    }
  };

  const handleRetakePhoto = () => {
    // Close crop modal and reopen webcam
    setCropModalOpen(false);
    setSelectedImageFile(null);
    setWebcamModalOpen(true);
  };

  const handleDicebearConfirm = avatarData => {
    // First call: { previewUrl, isUploading: true } - immediate preview
    // Second call: { largeUrl, thumbUrl, base64Thumb, attachmentId, isUploading: false } - final data
    // Error call: { isUploading: false } - upload failed
    if (!avatarData) return;

    const { previewUrl, isUploading, largeUrl, thumbUrl, base64Thumb, attachmentId } = avatarData;

    // Reset avatarRemoved since we're adding a new avatar
    setAvatarRemoved(false);

    if (isUploading) {
      // Immediate preview with DiceBear URL (before upload completes)
      setPreviewSrc(previewUrl);
      setDicebearModalOpen(false);
      setUploading(true);

      // CRITICAL FIX: Also save preview URL to additionalFields
      // This ensures the preview can be recovered if component remounts during upload
      setAdditionalFields?.(prev => ({
        ...prev,
        'avatar._deleted': undefined,
        'avatar.previewUrl': previewUrl, // Temporary preview URL (DiceBear URL)
      }));
    } else {
      // Upload finished (success or failure)
      setUploading(false);

      // Only update data if we have actual results (not a failure)
      if (largeUrl || thumbUrl || base64Thumb) {
        // IMPORTANT: Prefer base64Thumb for immediate display because:
        // 1. It's already converted to PNG (works in all browsers)
        // 2. Firebase SVG URLs may fail to load in some cases
        // 3. The HQ image will be loaded progressively in the background after save/reload
        const finalUrl = base64Thumb || thumbUrl || largeUrl;
        setPreviewSrc(finalUrl);
        setAdditionalFields?.(prev => ({
          ...prev,
          'avatar._deleted': undefined, // Clear any previous deletion flag
          'avatar.largeUrl': largeUrl,
          'avatar.thumbUrl': thumbUrl,
          'avatar.base64Thumb': base64Thumb,
          'avatar.attachmentId': attachmentId,
          'avatar.previewUrl': undefined, // Clear temporary preview URL
        }));
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7) RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  const AvatarInitialName = () => (
    <div
      className="jarvisly-initials-name-wrapper"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        background: backColor,
        color: foreColor,
        width: '90%',
        height: '90%',
        alignItems: 'center',
        borderRadius: 8,
        fontSize: 68,
        fontWeight: 600,
        marginLeft: 0.2,
        zIndex: 1,
        pointerEvents: 'none',
      }}
    >
      <div style={{ width: '100%' }} className="text-center jarvisly-initials-name-text">
        {initials}
      </div>
    </div>
  );

  const Watermark = () => {
    // Use neutral color when noShowColor is true
    const watermarkColor = noShowColor ? (isDarkMode ? '#666' : '#999') : foreColor || '#999';

    return (
      <div
        className="jarvisly-watermark text-uppercase"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          userSelect: 'none',
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: 2,
          color: watermarkColor,
          opacity: 0.6,
          zIndex: 1,
        }}
      >
        {translateX(avatarConfig?.placeholder || 'image')}
      </div>
    );
  };

  const toolbarForceVisible = dropdownOpen || colorPickerOpen;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8) UI
  // ─────────────────────────────────────────────────────────────────────────────
  const AvatarToolbar = (
    <div
      style={{
        position: 'absolute',
        bottom: -8,
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        gap: '1rem',
        background: 'rgba(0, 0, 0, 0.4)',
        padding: '0.5rem 1rem',
        borderRadius: 8,
        zIndex: 10,
      }}
    >
      <Tooltip title={translateX('add_a_picture')}>
        <Dropdown
          menu={{ items, onClick: onMenuClick }}
          trigger={['click']}
          placement="bottom"
          arrow={{ pointAtCenter: true }}
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
        >
          <CameraOutlined style={{ fontSize: 24, color: '#fff', cursor: 'pointer' }} />
        </Dropdown>
      </Tooltip>

      <Tooltip title={translateX('change_backgroud_color')}>
        <ColorPicker
          rootClassName="color-picker-small"
          defaultValue={backColor}
          size="small"
          onChange={onChangeColor}
          value={backColor}
          placement="bottom"
          open={colorPickerOpen}
          onOpenChange={setColorPickerOpen}
        />
      </Tooltip>
    </div>
  );

  return (
    <div className="text-center">
      <div
        className="rounded shadow-sm mx-auto show-avatar-toolbar"
        style={{
          marginTop: '-1.6rem',
          maxWidth: 158,
          maxHeight: 158,
          position: 'relative',
          // Ensure hover is detected on the entire container
          isolation: 'isolate',
          // Theme-aware background for avatar container border
          backgroundColor: isDarkMode ? DEFAULT_BACK_COLOR_ACCENT_DARK : DEFAULT_BACK_COLOR,
        }}
      >
        {/* Invisible hover layer to ensure consistent hover detection */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
          }}
        />
        <SimpleAvatar
          className="jarvisly-avatar-image"
          src={previewSrc || undefined}
          size={150}
          shape="square"
          backColor={
            noShowColor
              ? isDarkMode
                ? DEFAULT_BACK_COLOR_ACCENT_DARK
                : DEFAULT_BACK_COLOR_ACCENT
              : backColor
          }
          foreColor={noShowColor ? (isDarkMode ? '#666' : '#999') : foreColor}
          showBorder={true}
          showShadow={false}
          loading={uploading || isLoadingHQ}
          loadingName="cardHeader"
          shapeStyle={hideToolbar ? { padding: 8 } : undefined}
        />

        {/* initials when no image but we have a name (hidden if noShowInitials) */}
        {!previewSrc && hasInitials && !noShowInitials && <AvatarInitialName />}

        {/* watermark when no image and no initials (or when noShowInitials forces placeholder) */}
        {!previewSrc && (!hasInitials || noShowInitials) && <Watermark />}

        {/* toolbar (hidden if hideToolbar) */}
        {!lockAvatar && !hideToolbar && (
          <div
            key="avatar-toolbar"
            className={`avatar-toolbar-wrapper${toolbarForceVisible ? ' force-visible' : ''}`}
            style={{
              transition: 'opacity 0.4s ease-in-out',
              position: 'relative',
              zIndex: 20,
            }}
          >
            {AvatarToolbar}
          </div>
        )}

        {/* hidden upload trigger (gallery/file picker) */}
        <div ref={uploadRef} style={{ height: 0, overflow: 'hidden' }}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={beforeUpload}
            disabled={uploading}
          >
            <button type="button" style={{ display: 'none' }} />
          </Upload>
        </div>

        {/* hidden camera trigger (mobile only - uses capture attribute) */}
        <div ref={cameraRef} style={{ height: 0, overflow: 'hidden' }}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                beforeUpload(file);
              }
              // Reset input so same file can be selected again
              e.target.value = '';
            }}
          />
        </div>

        <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)}>
          <img alt="preview" style={{ width: '100%' }} src={previewSrc} />
        </Modal>

        <AvatarDicebearModal
          open={dicebearModalOpen}
          onCancel={() => setDicebearModalOpen(false)}
          onConfirm={handleDicebearConfirm}
        />

        <AvatarCropModal
          open={cropModalOpen}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
          imageFile={selectedImageFile}
          onRetakePhoto={imageFromWebcam ? handleRetakePhoto : undefined}
        />

        <WebcamCaptureModal
          open={webcamModalOpen}
          onCancel={() => setWebcamModalOpen(false)}
          onCapture={handleWebcamCapture}
        />
      </div>
    </div>
  );
};

// *******************************************************************************
// 8) HELPERS
// *******************************************************************************
function getValueByPath(obj, path) {
  const pathArray = Array.isArray(path) ? path : path.split('.');
  return pathArray.reduce((current, key) => current && current[key], obj);
}

export default CardHeaderAvatar;
